import Icon from "../../../Icon"
import { formatMoney } from "../../../../utils/format"
import { formatKsh, plural } from "./utilityAdminDisplay"

const TONES = {
  rose: { value: "text-rose-600", icon: "bg-rose-50 text-rose-600" },
  emerald: { value: "text-emerald-700", icon: "bg-emerald-50 text-emerald-600" },
  teal: { value: "text-gray-900", icon: "bg-teal-50 text-teal-600" },
  amber: { value: "text-gray-900", icon: "bg-amber-50 text-amber-600" },
}

function StatTile({ label, value, hint, icon, tone, onClick }) {
  const colors = TONES[tone] || TONES.teal
  const content = (
    <>
      <div className="flex items-start justify-between gap-2">
        <p className="text-[10px] font-semibold uppercase leading-tight tracking-wide text-gray-500">{label}</p>
        <span className={`flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-lg ${colors.icon}`}>
          <Icon name={icon} size="0.85rem" />
        </span>
      </div>
      <p className={`mt-1 text-sm font-bold leading-tight sm:text-base ${colors.value}`}>{value}</p>
      <p className="mt-0.5 text-[10px] leading-snug text-gray-400">{hint}</p>
    </>
  )
  const className = "rounded-xl border border-gray-100 bg-white p-3 text-left shadow-sm"
  if (!onClick) return <div className={className}>{content}</div>
  return (
    <button
      type="button"
      onClick={onClick}
      className={`${className} transition-colors hover:border-emerald-200 hover:bg-emerald-50/30 focus:outline-none focus:ring-2 focus:ring-emerald-300`}
    >
      {content}
    </button>
  )
}

function UtilityOverviewTiles({ summary = {}, period = {}, onOpenUtWallets, onOpenPropertyWallets }) {
  const utWallets = summary.utWallets || {}
  const propertyWallets = summary.propertyWallets || {}

  return (
    <div className="space-y-2">
      <div className="grid grid-cols-2 gap-2 lg:grid-cols-4">
        <StatTile
          label="Owed to companies"
          value={formatKsh(summary.owed)}
          hint={`All time · ${plural(summary.customersOwing || 0, "customer")} owing`}
          icon="receipt"
          tone="rose"
        />
        <StatTile
          label="Collected this month"
          value={formatKsh(summary.collectedThisMonth)}
          hint={`${period.monthLabel || "This month"} · payments received`}
          icon="graph-up-arrow"
          tone="emerald"
        />
        <StatTile
          label="People's wallets"
          value={formatKsh(utWallets.total)}
          hint={`${plural(utWallets.count || 0, "person", "people")} with money`}
          icon="wallet2"
          tone="teal"
          onClick={onOpenUtWallets}
        />
        <StatTile
          label="Landlord wallets"
          value={formatKsh(propertyWallets.total)}
          hint={`${plural(propertyWallets.count || 0, "property", "properties")} · Rentals income`}
          icon="house"
          tone="amber"
          onClick={onOpenPropertyWallets}
        />
      </div>

      <div className="rounded-xl border border-gray-100 bg-white shadow-sm">
        <div className="flex flex-wrap items-center justify-between gap-x-2 px-3 pt-2">
          <p className="text-[10px] font-semibold uppercase tracking-wide text-gray-500">All time (Ksh)</p>
          <p className="text-[10px] text-gray-400">
            {plural(summary.companies || 0, "company", "companies")} · {plural(summary.customers || 0, "customer")}
          </p>
        </div>
        <div className="grid grid-cols-3 divide-x divide-gray-100 py-2">
          {[
            { label: "Billed", value: summary.billed, tone: "text-gray-900" },
            { label: "Collected", value: summary.collected, tone: "text-emerald-700" },
            { label: "Waived", value: summary.waived, tone: "text-amber-600" },
          ].map((item) => (
            <div key={item.label} className="min-w-0 px-2 text-center">
              <p className="text-[10px] text-gray-400">{item.label}</p>
              <p className={`text-xs font-semibold leading-tight break-words ${item.tone}`}>{formatMoney(item.value || 0)}</p>
            </div>
          ))}
        </div>
        {Number(summary.credit || 0) > 0 && (
          <p className="border-t border-gray-50 px-3 py-1.5 text-[10px] text-gray-500">
            Customers also hold <span className="font-semibold text-emerald-700">{formatKsh(summary.credit)}</span> credit
            with companies for future bills.
          </p>
        )}
      </div>
    </div>
  )
}

export default UtilityOverviewTiles
